import { FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import type { IPencilOfferInputsIncentive, ManualIncentiveEntry, OfferIncentive } from '@makemydeal/dr-platform-types';
import { manualIncentiveUtils } from '@makemydeal/dr-platform-shared';

import { dateUtils } from '@makemydeal/dr-common-utils';
import isEmpty from 'lodash.isempty';
import { EXPLICIT_LENDER_P1_TOGGLE } from '../constants/meta';
import type { FrozenAwareIncentiveEntry, IncentiveToLookup, TermIncentive, TermIncentiveToTransform } from '../types/commonTypes';
import {
    MODIFY_MANUAL_INCENTIVE,
    PAYMENT_RECEIVED,
    SELECTED_TERM,
    UPDATED_MANUAL_INCENTIVES,
    UPDATE_SELECTED_INCENTIVES
} from '../types/actionTypes';
import { parseTermId } from './termIdParser';

const { formatUtcDateTimeToDateOnly } = dateUtils;

/**
 * DE760840: true when the term selection that triggered this payment response is no longer the
 * selected term, so the response's incentive data belongs to a term the user has moved off of.
 *
 * Payment requests and responses carry no correlating identifier, but `withMeta` attaches the
 * triggering action to every PAYMENT_RECEIVED, so a SELECTED_TERM origin can be compared against the
 * term currently in penciled inputs.
 *
 * Deliberately narrower than `meta.isStalePaymentResponse` (R-10, set only by leaseEndpointMiddleware):
 * that flag makes many reducers discard the response wholesale and is gated on
 * enableLeaseResidualTreatment_F286227. This predicate suppresses incentive writes only, and applies
 * regardless of feature toggle.
 *
 * Ids cannot be compared raw. They are re-keyed while the call is in flight — SELECTED_TERM dispatches
 * `lease_term_48` and the id becomes `lease_term_48_downpayment_4500_01` once the payment lands (the same
 * re-keying `findCatalogForReKeyedTerm` exists for) — so raw equality reports every term change as
 * superseded. Comparing months alone is the opposite error: two lease terms can share a month count and
 * differ by mileage or down payment, and they price incentives differently, so a response for 36/10k landing
 * after the user moved to 36/12k would not be flagged and would merge into the wrong variant.
 *
 * So compare every parsed param the two ids have in common. The requested id is a prefix of the settled one,
 * which makes the shared set exactly the params both sides actually assert.
 */
const sharedParamsDiffer = (requestedTermId: string, currentTermId: string): boolean | undefined => {
    const requested = parseTermId(requestedTermId);
    const current = parseTermId(currentTermId);
    const requestedParams = requested?.termIdParams;
    const currentParams = current?.termIdParams;
    if (!requestedParams || !currentParams) {
        return undefined;
    }
    if (requested.offerType && current.offerType && requested.offerType !== current.offerType) {
        return true;
    }
    const shared = Object.keys(requestedParams).filter((key) => currentParams[key] !== undefined);
    if (shared.length === 0) {
        return undefined;
    }
    return shared.some((key) => requestedParams[key] !== currentParams[key]);
};

export const isSupersededTermPaymentResponse = (action: any, currentTermId?: string | null): boolean => {
    const originalAction = action?.meta?.originalAction;
    if (originalAction?.type !== SELECTED_TERM) {
        return false;
    }
    const requestedTermId = originalAction.payload;
    if (!requestedTermId || !currentTermId) {
        return false;
    }
    if (requestedTermId === currentTermId) {
        return false;
    }
    const differs = sharedParamsDiffer(requestedTermId, currentTermId);
    // Neither id parsed, or they share no asserted params, so there is nothing to compare. Suppressing the
    // incentive write is the recoverable failure: the next response re-derives it, whereas merging the wrong
    // term's incentive is this defect and it persists to the saved deal.
    return differs ?? true;
};

/**
 * DE760840 (AC1): true for the payment response that settles a term change and is still current.
 *
 * The applied incentive snapshot is re-priced on this response rather than on SELECTED_TERM itself,
 * because the newly selected term's `appliedIncentives` set is empty until a response populates it —
 * re-deriving at selection time zeroes the incentive instead of re-pricing it.
 *
 * Restricted to term-change-triggered responses so the snapshot is not rewritten on unrelated recalcs.
 */
export const shouldRederiveAppliedIncentivesForTerm = (action: any, currentTermId?: string | null): boolean =>
    action?.type === PAYMENT_RECEIVED &&
    action?.meta?.originalAction?.type === SELECTED_TERM &&
    !action?.meta?.isStalePaymentResponse &&
    !isSupersededTermPaymentResponse(action, currentTermId);

/**
 * Map a legacy incentive data entry to the new manual rebate data model.
 * @param incentive (applied incentive; conditional or non-conditional)
 * @returns
 */
export const transformToManualIncentive = (incentive: ManualIncentiveEntry): ManualIncentiveEntry => {
    const manualEntry = manualIncentiveUtils.transformToBaseIncentive(incentive);

    return {
        ...manualEntry,
        deleted: false,
        manual: false
    };
};

/**
 *
 * @param incentives: all manual incentives
 * @returns total value of all incentives not including those flagged for deletion.
 */
export const getIncentiveSum = (incentives: ManualIncentiveEntry[]) =>
    incentives.reduce((total, { amount, deleted }) => {
        if (!deleted) {
            return (total += amount);
        }
        return total;
    }, 0);

export const parseIncentiveId = (name: string | null | undefined): number => {
    if (name == null) {
        return NaN;
    }
    const suffix = String(name).trim().split('_').at(-1);
    return suffix ? Number(suffix) : NaN;
};

export const resolveIncentiveKey = (incentive: any): string | null => {
    const codeOrPdId = incentive?.code ?? incentive?.pdId;
    if (codeOrPdId != null && codeOrPdId !== '') {
        return String(codeOrPdId);
    }
    if (incentive?.id != null) {
        const parsed = parseIncentiveId(String(incentive.id));
        return Number.isNaN(parsed) ? null : String(parsed);
    }
    return null;
};

export const resolveIncentiveNumericId = (incentive: any): number | null => {
    const key = resolveIncentiveKey(incentive);
    if (key == null) {
        return null;
    }
    const numericKey = Number(key);
    return Number.isFinite(numericKey) ? numericKey : null;
};

/**
 * DE760840: price list for one term.
 *
 * Keyed by the side-qualified id (`conditional:{code}` / `nonconditional:{code}`) rather than the bare
 * numeric code. Both term arrays can carry the same numeric id, and folding them into one keyspace lets a
 * conditional overwrite a nonConditional purely by spread order — the same "don't fold incentives by code"
 * conclusion DE761820 / DE758866 landed on.
 */
const CONDITIONAL_SIDE = 'conditional';
const NONCONDITIONAL_SIDE = 'nonconditional';

const priceMapKey = (side: string, key: string | number): string => `${side}:${key}`;

/**
 * A row does not record which side it came from. `conditionCategory` is the only conditional marker on a row
 * and Payment Services does not always send it, so the preferred side is a guess and the other side is a
 * fallback.
 *
 * The fallback cannot cross a code that exists on both sides: if it did, the preferred lookup would have
 * found a price and returned already. So the fallback only ever resolves a code unique to one side, which is
 * the guarantee the side-qualified keyspace exists for — no extra check needed to enforce it.
 */
const priceForRow = (row: ManualIncentiveEntry, key: string | number, priceMap: TermIncentivePriceMap): number | undefined => {
    const preferred = row.conditionCategory ? CONDITIONAL_SIDE : NONCONDITIONAL_SIDE;
    const other = preferred === CONDITIONAL_SIDE ? NONCONDITIONAL_SIDE : CONDITIONAL_SIDE;
    return priceMap.priceByKey.get(priceMapKey(preferred, key)) ?? priceMap.priceByKey.get(priceMapKey(other, key));
};

/**
 * A row's per-term price band, which lives under `termRebates` on some shapes and `termIncentives` on
 * others. Single source for that pair so callers cannot disagree about which key to read.
 */
export const resolveIncentiveTermRange = (incentive: any): any[] | undefined =>
    (incentive?.termRebates || incentive?.termIncentives)?.incentiveTermRange;

export type TermIncentivePriceMap = {
    priceByKey: Map<string, number>;
    /** Codes appearing more than once within one side, so no single price is authoritative. */
    ambiguous: Set<string>;
};

/**
 * DE760840: price list for one term.
 *
 * A code repeated *within* one side is dropped rather than resolved last-in-wins. That shape is real — a
 * dealer may enter several manual rows reusing one program code with different amounts, so equal codes do
 * not mean equal rows (DE761820, `incentiveSelectors` "must not deduplicate by incentive code"). Picking the
 * last would let a user-entered figure re-price a program row, and would be order-dependent besides.
 */
export const buildTermIncentivePriceMap = (term: any): TermIncentivePriceMap => {
    const priceByKey = new Map<string, number>();
    const ambiguous = new Set<string>();
    const seenPerSide = new Set<string>();
    const sides: [string, any[]][] = [
        [NONCONDITIONAL_SIDE, term?.incentives?.nonConditionals ?? []],
        [CONDITIONAL_SIDE, term?.incentives?.conditionals ?? []]
    ];
    for (const [side, entries] of sides) {
        for (const entry of entries) {
            const key = resolveIncentiveKey(entry);
            if (key == null || typeof entry.amount !== 'number') {
                continue;
            }
            const mapKey = priceMapKey(side, key);
            if (seenPerSide.has(mapKey)) {
                ambiguous.add(String(key));
                priceByKey.delete(mapKey);
                continue;
            }
            seenPerSide.add(mapKey);
            priceByKey.set(mapKey, entry.amount);
        }
    }
    return { priceByKey, ambiguous };
};

/**
 * DE760840: true when the row carries its own per-term price band and no concrete amount.
 *
 * `transformIncentiveAmount` returns `amount` whenever it is truthy and otherwise resolves the band, so
 * stamping an amount onto such a row permanently disables that resolution for every later term. The band is
 * already term-aware and is the better authority, so leave the row alone. (A row that already has a truthy
 * amount has bypassed its band since before this fix — re-pricing that one changes nothing about
 * reachability.)
 */
const hasUnresolvedTermBand = (row: ManualIncentiveEntry): boolean => {
    const bands = resolveIncentiveTermRange(row);
    return Array.isArray(bands) && bands.length > 0 && !row.amount;
};

/**
 * DE760840: re-price program-derived rows from a term's price list, in place.
 *
 * Rows are never added or removed. A code the term does not price keeps its existing amount — a missing
 * price is not evidence of $0, and zeroing on missing catalog data is what produces the $0.00 display this
 * defect reports.
 *
 * Provenance is taken from `manual` plus the presence of a resolvable code, matching how the payment payload
 * decides a row is program-derived, so display and request agree by construction. Inferring it instead from
 * `type`/`isStandardIncentive`/`conditionCategory` skips a genuine program conditional whenever Payment
 * Services omits `conditionCategory`, leaving the stale amount in place — the defect, silently, on data we
 * do not control.
 */
export const repriceRowsFromPriceMap = <T extends ManualIncentiveEntry>(
    rows: T[],
    priceMap: TermIncentivePriceMap
): { rows: T[]; changed: boolean } => {
    let changed = false;
    const repriced = rows.map((row) => {
        const key = resolveIncentiveKey(row);
        // A user's own figure — entered or overridden — is authoritative; a row with no resolvable code
        // cannot be matched to a term price anyway. An ambiguous code has no single authoritative price.
        if (isUserCustomIncentive(row) || key == null || hasUnresolvedTermBand(row) || priceMap.ambiguous.has(String(key))) {
            return row;
        }
        const priced = priceForRow(row, key, priceMap);
        if (priced == null || priced === row.amount) {
            return row;
        }
        changed = true;
        return { ...row, amount: priced };
    });
    return { rows: repriced, changed };
};

export const isProgramIncentivesRestorationEnabled = (state: any): boolean =>
    Boolean(state?.featureToggles?.enableProgramIncentivesRestoration_F292564);

export const isAppliedIncentiveDisplayFixesEnabled = (state: any): boolean =>
    Boolean(state?.featureToggles?.DE765882_Enable_Applied_Incentive_Display_Fixes);

export const isKeepLenderOnZipChangeEnabled = (state: any): boolean =>
    Boolean(state?.featureToggles?.DE778958_Enable_Keep_Lender_On_Zip_Change);

export const isResponseAppliedIncentiveAmountEnabled = (state: any): boolean =>
    Boolean(state?.featureToggles?.DE778704_Enable_Response_Applied_Incentive_Amount);

export const isRollToApplyIncentiveCatalogEnabled = (state: any): boolean =>
    Boolean(state?.featureToggles?.DE779915_Enable_Roll_To_Apply_Incentive_Catalog);

export const isVehicleSwitchStandardsHandoffEnabled = (state: any): boolean =>
    Boolean(state?.featureToggles?.DE780005_Enable_Vehicle_Switch_Standards_Handoff);

export const isCashSwitchStandardRebateEnabled = (state: any): boolean =>
    Boolean(state?.featureToggles?.DE783865_Enable_Cash_Switch_Standard_Rebate);

export const isEditedProgramIncentiveDedupeEnabled = (state: any): boolean =>
    Boolean(state?.featureToggles?.DE777671_Enable_Edited_Program_Incentive_Dedupe);

/** Gates the UPQ rebate ledger: the applied rebate resolves from the penciled bag, not the term record. */
export const isUpqRebateLedgerEnabled = (state: any): boolean => Boolean(state?.featureToggles?.DE771128_Enable_UPQ_Rebate_Ledger);

export const isUpqRebateLedgerInEffect = (state: any, offerType?: string): boolean =>
    isUpqRebateLedgerEnabled(state) || isGridIncentiveManagementEnabled(state, offerType);

// Gates every DE759607/US2076398 cash-deal-incentives fix in offer-redux.
export const isCashDealIncentivesFixesEnabled = (state: any): boolean =>
    Boolean(state?.featureToggles?.US2076398_Enable_Cash_Deal_Incentives_Fixes);

// Gates asking Payment Services to report the standards it was told to pick (DE768653).
export const isRequestChosenStandardsEnabled = (state: any): boolean =>
    Boolean(state?.featureToggles?.DE768653_Enable_Request_Chosen_Standards);

// Not `??`: the global slice holds an explicit `false` for a toggle unconfigured in AWS, shadowing a
// `?enableToggles=` override, which lands on the offer slice.
const readEitherSlice = (state: any, toggle: string): boolean =>
    Boolean(state?.featureToggles?.[toggle]) || Boolean(state?.offer?.featureToggles?.[toggle]);

export const isSkipNullCashIncentiveIdsEnabled = (state: any): boolean =>
    readEitherSlice(state, 'DE768913_Skip_Null_Cash_Incentive_Ids');

/**
 * On INIT_OFFER the toggle is only on the action payload: the featureToggles slice has not been filled
 * yet, because INIT_PENCIL_SUCCESS is mapped to INIT_OFFER before it reaches that reducer.
 */
export const isSkipNullCashIncentiveIdsEnabledForAction = (state: any, action: { payload?: any }): boolean =>
    isSkipNullCashIncentiveIdsEnabled(state) || isSkipNullCashIncentiveIdsEnabled(action.payload);

export const isUnpricedTermStandardsCarryEnabled = (state: any): boolean =>
    readEitherSlice(state, 'DE771157_Enable_Unpriced_Term_Standards_Carry');

export const isManualRebatePreservationEnabled = (state: any): boolean =>
    readEitherSlice(state, 'DE773201_Enable_Manual_Rebate_Preservation');

// Gates building offer.incentives for cash from the penciled catalog instead of the
// PAYMENT_RECEIVED payload (DE771098).
export const isConditionalIncentivesTotalCashDealLoadEnabled = (state: any): boolean =>
    Boolean(state?.featureToggles?.DE771098_Use_Conditional_Incentives_Total_Cash_Deal_Load);

export const isKeepCashIncentiveCatalogDetailEnabled = (state: any): boolean =>
    readEitherSlice(state, 'DE778947_Keep_Cash_Incentive_Catalog_Detail');

export const isCashIncentiveNameOnCatalogLoadEnabled = (state: any): boolean =>
    readEitherSlice(state, 'DE782844_Enable_Cash_Incentive_Name_On_Catalog_Load');

// Gates classifying cash rows for the payment request by program provenance instead of by row fields
// (DE783585). Off means the pre-fix behaviour, double count included - see isManualByProvenanceForRequest.
export const isCashManualRebateProvenanceEnabled = (state: any): boolean =>
    readEitherSlice(state, 'DE783585_Enable_Cash_Manual_Rebate_Provenance');

export const isCashRebateLedgerSeedEnabled = (state: any): boolean =>
    readEitherSlice(state, 'DE783843_Enable_Cash_Rebate_Ledger_Seed');

// Gates keeping a selected grid cell priced on a rebate the ledger no longer holds off the selected term (DE785177).
export const isGridRebateIdsConsistencyEnabled = (state: any): boolean =>
    readEitherSlice(state, 'DE785177_Enable_Grid_Rebate_Ids_Consistency');

export const isGridCashDownCatalogCarryEnabled = (state: any): boolean =>
    readEitherSlice(state, 'DE773203_Enable_Grid_Cash_Down_Catalog_Carry');

export const isUpqProgramLossBypassEnabled = (state: any): boolean =>
    readEitherSlice(state, 'DE773480_Bypass_Program_Loss_Guard_On_Upq');

export const isSendVehicleFieldsOnPaymentEnabled = (state: any): boolean =>
    readEitherSlice(state, 'DE782162_Send_Vehicle_Fields_On_Payment');

export const isGridManualRebateMainRowFirstEnabled = (state: any): boolean =>
    readEitherSlice(state, 'DE774239_Enable_Grid_Manual_Rebate_Main_Row_First');

export const isGridIncentiveManagementEnabled = (state: any, offerType: unknown): boolean =>
    (offerType === FINANCE || offerType === LEASE) &&
    readEitherSlice(state, EXPLICIT_LENDER_P1_TOGGLE) &&
    readEitherSlice(state, 'F319474_Enable_Grid_Incentive_Management');

const INCENTIVE_CHANGE_ACTION_TYPES = new Set<string>([
    UPDATE_SELECTED_INCENTIVES,
    MODIFY_MANUAL_INCENTIVE,
    UPDATED_MANUAL_INCENTIVES
]);

export const isIncentiveChangeActionType = (actionType: unknown): boolean =>
    INCENTIVE_CHANGE_ACTION_TYPES.has(actionType as string);

export const isConditionalRebateKeepGridOpenEnabled = (state: any): boolean =>
    readEitherSlice(state, 'DE774439_Enable_Conditional_Rebate_Keep_Grid_Open');

export const isHonorDealerStandardExclusionEnabled = (state: any): boolean =>
    readEitherSlice(state, 'DE773664_Honor_Dealer_Standard_Exclusion');

export const isUpqAllInPriceIncentivesEnabled = (state: any): boolean =>
    readEitherSlice(state, 'DE773365_Enable_UPQ_All_In_Price_Incentives');

export const buildConditionalIncentiveKey = (offerType: string, id: number | string): string => `conditional_${offerType}_${id}`;

export const buildNonConditionalIncentiveKey = (offerType: string, id: number | string): string =>
    `nonconditional_${offerType}_${id}`;

export const NONE_SELECTED = 'none';
export const NON_CONDITIONAL_NONE_SELECTED = NONE_SELECTED;
export const CONDITIONAL_NONE_SELECTED = NONE_SELECTED;

export const buildNonConditionalSelectionKeys = (offerType: string, ids: number[], recordEmptySelection: boolean): string[] => {
    if (ids.length > 0) {
        return ids.map((id) => buildNonConditionalIncentiveKey(offerType, id));
    }
    return recordEmptySelection ? [buildNonConditionalIncentiveKey(offerType, NON_CONDITIONAL_NONE_SELECTED)] : [];
};

export const buildConditionalSelectionKeys = (offerType: string, ids: number[], recordEmptySelection: boolean): string[] => {
    if (ids.length > 0) {
        return ids.map((id) => buildConditionalIncentiveKey(offerType, id));
    }
    return recordEmptySelection ? [buildConditionalIncentiveKey(offerType, CONDITIONAL_NONE_SELECTED)] : [];
};

export const toValidIncentiveCode = (code?: number | string | null): number | undefined => {
    if (code == null || code === '') return undefined;
    const numericCode = Number(code);
    return Number.isFinite(numericCode) && numericCode > 0 ? numericCode : undefined;
};

export const isDefinedIncentiveCode = (code: number | undefined): code is number => code !== undefined;

export const isDealerCash = ({ type }: ManualIncentiveEntry): boolean => type === 'DealerCash';
export const isNotDealerCash = ({ type }: ManualIncentiveEntry): boolean => type !== 'DealerCash';

type StoredIncentiveShape = { type?: string; incentiveType?: string };

export const resolveIncentiveType = (incentive: StoredIncentiveShape): string | undefined =>
    incentive?.type ?? incentive?.incentiveType;

export const isStoredDealerCash = (incentive: StoredIncentiveShape): boolean => resolveIncentiveType(incentive) === 'DealerCash';
export const isNotStoredDealerCash = (incentive: StoredIncentiveShape): boolean => !isStoredDealerCash(incentive);

/**
 * The rebate total of a ledger's rows, dealer cash excluded. Read from the rows rather than `totalRebates`, which
 * only the reducer's own writes fill: the bag acceleron sends on init carries rows without it.
 */
export const sumLedgerRebates = (incentives: ManualIncentiveEntry[]): number =>
    incentives.filter(isNotStoredDealerCash).reduce((sum, incentive) => sum + (incentive.amount || 0), 0);

const MANUAL_INCENTIVE_TYPES = new Set(['CustomerCash', 'DealerCash']);

export const isUserEnteredManualIncentive = (incentive: ManualIncentiveEntry): boolean =>
    MANUAL_INCENTIVE_TYPES.has(incentive.type as string) && !incentive.isStandardIncentive && !incentive.conditionCategory;

export const isUserCustomIncentive = (incentive: ManualIncentiveEntry): boolean => incentive.manual === true;

export const isManualRebateForRequest = (incentive: ManualIncentiveEntry): boolean =>
    isUserEnteredManualIncentive(incentive) || isUserCustomIncentive(incentive);

/**
 * A manual row that is the dealer's edit of a program incentive: editing copies the program row, so it keeps the
 * program's description, while a row the dealer adds from scratch starts with an empty one. Same test as
 * `isProgramIncentiveOverride` in activities-programs. The Rebates screen skips dealer cash rows in its caller before
 * asking, which this helper does not, so an edited dealer cash program row counts here. Unlike
 * `getOverriddenIncentiveCodes` (any manual row with a code), a row added with no description does not count.
 */
export const isProgramOverrideIncentive = (incentive: ManualIncentiveEntry): boolean => {
    const description = incentive.description ?? '';
    return isUserCustomIncentive(incentive) && description !== '' && description !== incentive.name;
};

/**
 * A manufacturer (standard) program incentive, as opposed to a conditional program incentive or a
 * dealer-entered rebate. Vehicle-switch item retention drops these rows (`getIncentivesForRetentionModal`):
 * a standard is quoted against the vehicle it belongs to, so a switch resolves the new vehicle's own
 * standards rather than carrying one over.
 *
 * `manual !== true` rather than `manual === false` on purpose: a row of unknown provenance that carries
 * the standard flag counts as a standard. `isStandardIncentive` is only ever set from a program/payment
 * response, so a row carrying it did come from a program; the one row that must never be dropped is the
 * dealer-entered one, and `manual === true` is exactly what marks it.
 */
export const isProgramStandardIncentive = (incentive: ManualIncentiveEntry): boolean =>
    !isUserCustomIncentive(incentive) && Boolean(incentive.isStandardIncentive);

/**
 * True for a program-sourced rebate frozen into the penciled bag. See `FrozenAwareIncentiveEntry` for the
 * three-provenance model.
 *
 * Checks the marker alone, not `manual === false` as well, so a row promoted to a user row stops being
 * frozen even if some other path leaves `manual` untouched.
 */
export const isFrozenProgramRow = (incentive: ManualIncentiveEntry): boolean =>
    Boolean((incentive as FrozenAwareIncentiveEntry)?.frozenFromProgram);

export const isManualIncentiveByProvenance = (incentive: ManualIncentiveEntry): boolean =>
    isUserCustomIncentive(incentive) ||
    (isUserEnteredManualIncentive(incentive) && !isFrozenProgramRow(incentive) && resolveIncentiveKey(incentive) == null);

export const getOverriddenIncentiveCodes = (
    incentives: ReadonlyArray<{ code?: number | string | null; manual?: boolean }>
): Set<number> =>
    new Set(
        (incentives ?? [])
            .filter((incentive) => incentive.manual)
            .map(resolveIncentiveNumericId)
            .filter((id): id is number => id != null)
    );

/**
 * Codes to exclude from a program request's conditional/nonConditional arrays. Covers incentives
 * flagged `manual`, plus user-entered rebates that carry `manual: false` yet are not program
 * incentives (identified by {@link isUserEnteredManualIncentive}). Program incentives are matched
 * downstream by the term's compatibleIncentiveIds, which user-entered ones never appear in.
 */
export const getManualIncentiveCodesToExclude = (incentives: ReadonlyArray<ManualIncentiveEntry>): Set<number> =>
    new Set(
        (incentives ?? [])
            .filter((incentive) => incentive.manual || isUserEnteredManualIncentive(incentive))
            .map(resolveIncentiveNumericId)
            .filter((id): id is number => id != null)
    );

// Transform the payment incentive based on terms.
export function transformTermIncentive(term: number, incentive: TermIncentiveToTransform) {
    const newIncentive: TermIncentive = {
        id: incentive.incentiveId,
        amount: incentive.cash
    };
    // @note Cash term is -1 since we don't have a term for cash deals
    const isCashDeal = term === -1;

    const termRangeList = incentive?.termIncentives?.incentiveTermRange || [];

    if (newIncentive.amount !== 0) {
        return newIncentive;
    }

    if (isCashDeal) {
        newIncentive.amount = termRangeList.at(0)?.customerCash || 0;
    } else {
        termRangeList.forEach((termRange) => {
            if (term >= termRange.startTerm && term <= termRange.endTerm) {
                newIncentive.amount = termRange.customerCash;
                if (termRange.apr && termRange.apr !== 0) {
                    newIncentive.apr = termRange.apr;
                }
            }
        });
    }

    return newIncentive;
}

export function transformIncentivesForPayment(term: number, incentivesArray: TermIncentiveToTransform[] = []) {
    const incentivePaymentList = incentivesArray.map((incentive: TermIncentiveToTransform) => {
        const transformedIncentives = transformTermIncentive(term, incentive);
        return transformedIncentives;
    });

    return incentivePaymentList;
}

// Takes applied incentives and sorts them into payments.
export function transformAppliedIcentivesForPayment(term: number, incentivesLookup: any, appliedIncentiesForPayment: any) {
    const incentivePaymentList: number[] = [];
    appliedIncentiesForPayment.forEach((appliedIncentive: any) => {
        const incentive = incentivesLookup[appliedIncentive.toString()];

        if (incentive) {
            incentivePaymentList.push(transformTermIncentive(term, incentive).id);
        }
    });

    return incentivePaymentList;
}

// Transforms the incentives for a response and creates a lookup for payments
// to easier lookup incentives.
export function transformIncentives(incentives: any) {
    if (isEmpty(incentives)) {
        return {
            incentivesToAdd: [] as OfferIncentive[],
            incentivesLookup: {} as IncentiveToLookup
        };
    }
    // Creating a lookup to speed up future searches for incentives which has to be done in payment.
    const incentivesLookup: IncentiveToLookup = {};
    const incentivesToAdd: OfferIncentive[] = [];
    incentives.forEach((incentive: any) => {
        const newIncentive = {
            id: incentive.incentiveId,
            name: incentive.programName,
            amount: incentive.cash || 0,
            description: incentive.description,
            conditionCategory: incentive.conditionCategory,
            exclusions: incentive.exclusions,
            pdId: incentive.incentiveId,
            startDate: formatUtcDateTimeToDateOnly(incentive.startDate),
            endDate: formatUtcDateTimeToDateOnly(incentive.endDate),
            programId: incentive.programId || '',
            isStandardIncentive: incentive.isStandardIncentive,
            incentiveType: incentive.incentiveType
        };

        incentivesLookup[incentive.incentiveId.toString()] = incentive;
        incentivesToAdd.push(newIncentive);
    });

    return {
        incentivesToAdd,
        incentivesLookup
    };
}

export function transformCashIncentives(incentives: any) {
    if (isEmpty(incentives)) {
        return {
            conditionals: [],
            nonConditionals: []
        };
    }
    const conditionalIncentivesResults = transformIncentives(incentives.conditional);
    const nonConditionalIncentivesResults = transformIncentives(incentives.nonConditional);

    const incentivesToAdd = {
        conditionals: conditionalIncentivesResults.incentivesToAdd,
        nonConditionals: nonConditionalIncentivesResults.incentivesToAdd
    };

    return incentivesToAdd;
}

type TermIncentiveWithExclusions = IPencilOfferInputsIncentive & {
    exclusions?: number[];
    name?: string;
    program?: string;
    type?: string;
    isStandardIncentive?: boolean;
    programId?: string;
    incentiveType?: string;
    description?: string;
};

export const updateTermIncentives = (incentives: [], prefix: string, includeDetails = false, dedupeById = false) => {
    const results: TermIncentiveWithExclusions[] = [];
    const seenIds = new Set<string>();

    incentives?.forEach((incentive) => {
        const { id, amount, exclusions, name, program, type, isStandardIncentive, description } = (incentive ??
            {}) as TermIncentiveWithExclusions;
        if (!isEmpty(incentive) && id) {
            const prefixedId = `${prefix}${id}`;
            if (dedupeById && seenIds.has(prefixedId)) {
                return;
            }
            seenIds.add(prefixedId);
            results.push({
                id: prefixedId,
                amount,
                ...(exclusions && { exclusions }),
                ...(includeDetails && {
                    ...(name != null && { name }),
                    ...(program != null && { programId: program }),
                    ...(type != null && { incentiveType: type }),
                    ...(isStandardIncentive != null && { isStandardIncentive }),
                    ...(description != null && { description })
                })
            });
        }
    });

    return results;
};
